// e1c/src/App.js
// Author: Sunil Jain
// Updated to consolidate all providers for stability.

import React, { useState, useEffect } from 'react'; 
import { BrowserRouter, Routes, Route, Outlet, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Box, CircularProgress, Typography, Alert, Drawer, ThemeProvider, CssBaseline } from '@mui/material';
import FHIR from 'fhirclient';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'; // <<< 1. ADD THIS IMPORT

// Core Providers & Components
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { AriaContextProvider } from './contexts/AriaContext';
import ErrorBoundary from './components/ErrorBoundary';
import ProtectedRoute from './components/ProtectedRoute';
import Navbar, { drawerWidth } from './components/Navbar';
import AssistantWidget from './components/AssistantWidget';
import theme from './theme';
import logger from './utils/logger';

// Page/Component Imports
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import StaffRegistration from './pages/StaffRegistration';
import CreateOrganizationPage from './pages/CreateOrganizationPage';
import Dashboard from './pages/Dashboard';
import NotificationsPage from './pages/NotificationsPage';
import LaunchPage from './pages/LaunchPage';
import PhysicianProfile from './pages/PhysicianProfile';
import PatientDetail from './pages/PatientDetail';
import ContentStudio from './pages/ContentStudio';
import OnboardingTraining from './pages/OnboardingTraining';
import TenantSettings from './pages/TenantSettings';
import NotFound from './pages/NotFound';
import TherapeuticEpisodesManagementPage from './pages/TherapeuticEpisodesManagementPage';
import SurveyCampaignsManagementPage from './pages/SurveyCampaignsManagementPage';
import Tasks from './pages/Tasks.js';

// --- 2. CREATE THE QUERY CLIENT INSTANCE (moved from index.js) ---
const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: 1000 * 60 * 5,
            gcTime: 1000 * 60 * 30,
            refetchOnWindowFocus: false,
            retry: 1,
        },
    },
});

// --- (The Layout and SmartLaunchStatus components remain unchanged) ---

const assistantWidth = 400;

const Layout = () => {
  // ... (Layout code is unchanged)
  logger.debug("Rendering Layout component for protected routes.");
  const [isAssistantOpen, setIsAssistantOpen] = useState(false);
  const handleToggleAssistant = () => setIsAssistantOpen(prev => !prev);

  return (
    <Box sx={{ display: 'flex' }}>
      <Navbar onToggleAssistant={handleToggleAssistant} />
      <Box 
        component="main" 
        sx={{ 
            flexGrow: 1, 
            p: { xs: 2, sm: 3 }, 
            width: { sm: `calc(100% - ${drawerWidth}px)` }, 
            position: 'relative', 
            mt: { xs: '56px', sm: '64px' } 
        }}
      >
        <Outlet /> 
      </Box>
      <Drawer
        anchor="right"
        open={isAssistantOpen}
        onClose={() => setIsAssistantOpen(false)}
        sx={{ '& .MuiDrawer-paper': { width: '100%', maxWidth: assistantWidth, boxSizing: 'border-box', backgroundImage: 'none' } }}
      >
         <AssistantWidget externallyControlledOpen={isAssistantOpen} />
      </Drawer>
    </Box>
  );
};

const SmartLaunchStatus = ({ message, error }) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '80vh', p: 3 }}>
        {!error && <CircularProgress sx={{ mb: 2 }} />}
        <Typography variant="h6" gutterBottom>{message || 'Processing EHR Launch...'}</Typography>
        {error && (<Alert severity="error" sx={{ mt: 2, width: '100%', maxWidth: '600px' }}>{error}</Alert>)}
    </Box>
);


// --- Main App Component with All Providers ---
function App() {
  return (
    // --- 3. WRAP YOUR APP WITH THE QUERY CLIENT PROVIDER ---
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <ErrorBoundary>
          <BrowserRouter basename="/e1c">
            <AuthProvider>
              <MainApp />
            </AuthProvider>
          </BrowserRouter>
        </ErrorBoundary>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

// --- (The MainApp component with routing logic remains unchanged) ---
function MainApp() {
  logger.debug("MainApp component rendering - MedsAi Physician Portal");
  const navigate = useNavigate();
  const location = useLocation();
  const { setSmartClient } = useAuth();
  const [isSmartAuthLogicActive, setIsSmartAuthLogicActive] = useState(
    () => location.pathname === '/' && (location.search.includes('code=') || location.search.includes('error='))
  );
  const [smartAuthOutcome, setSmartAuthOutcome] = useState({ message: 'Checking EHR launch status...', error: null });

  useEffect(() => {
    if (location.pathname === '/' && (location.search.includes('code=') || location.search.includes('error='))) {
      if (isSmartAuthLogicActive) { 
        logger.info("App: Detected SMART redirect. Calling FHIR.oauth2.ready()...");
        setSmartAuthOutcome({ message: 'Completing EHR authorization...', error: null });
        FHIR.oauth2.ready()
            .then(client => {
                logger.info("App: FHIR.oauth2.ready() successful! Client ready.");
                setSmartAuthOutcome({ message: 'EHR Connection Established. Loading Application...', error: null });
                setSmartClient(client); 
                navigate(client.state?.redirect_url || '/dashboard', { replace: true });
                setIsSmartAuthLogicActive(false); 
            })
            .catch(err => {
                logger.error("App: FHIR.oauth2.ready() failed.", err);
                setSmartAuthOutcome({ message: 'EHR Authorization Failed.', error: `Failed to complete EHR authorization: ${err.message || 'Unknown error'}. Please ensure you launched from the EHR.` });
                setIsSmartAuthLogicActive(false); 
            });
      }
    } else {
        if(isSmartAuthLogicActive) setIsSmartAuthLogicActive(false);
    }
  }, [location, setSmartClient, navigate, isSmartAuthLogicActive]); 

  if (isSmartAuthLogicActive || (smartAuthOutcome.error && location.pathname === '/')) {
    return <SmartLaunchStatus message={smartAuthOutcome.message} error={smartAuthOutcome.error} />;
  }
  
  return (
    <Routes>
      <Route path="/launch" element={<LaunchPage />} />
      <Route path="/login" element={<Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password/:token" element={<ResetPassword />} />
      <Route path="/register-organization" element={<CreateOrganizationPage />} />
      <Route path="/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
      <Route path="/register-staff" element={<StaffRegistration />} />
      
      <Route path="/onboarding-training" element={<ProtectedRoute requiresTraining={false}><Box sx={{ p: 3, mt: 8, maxWidth: 'md', mx: 'auto' }}><OnboardingTraining /></Box></ProtectedRoute>} />

      <Route path="/" element={<ProtectedRoute><AriaContextProvider><Layout /></AriaContextProvider></ProtectedRoute>}>
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="profile" element={<PhysicianProfile />} />
        <Route path="patients/:patientUserId/*" element={<PatientDetail />} /> 
        <Route path="content-studio" element={<ContentStudio />} />
        <Route path="admin/tenant-settings" element={<TenantSettings />} />
        <Route path="therapeutic-episodes-management" element={<TherapeuticEpisodesManagementPage />} />
        <Route path="survey-campaigns-management" element={<SurveyCampaignsManagementPage />} />
        <Route path="tasks" element={<Tasks />} />
      </Route>

      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

export default App;